/**
 * The client's month as a list (Calendar / Client post list, Pen r9LhBH): the
 * brand block, the month and one sentence, then For your review, Coming up and
 * Approved, and the reader at the foot. While the client comments the list
 * folds to a 72px column of dates (Calendar / Client rail · Folded, Nttnv),
 * and on a phone it is the home screen under Bar / Client top · Phone (YraR4).
 */
import { ChevronRight, MessageSquare, PanelLeftOpen } from "lucide-react";
import { cn } from "@/utils/helpers";
import { Avatar } from "@/app/routes/dashboard/content-calendar/comments/CommentThread";
import { initials } from "@/app/routes/dashboard/content-calendar/comments/commentModel";
import {
  AGENCY,
  rowMeta,
  turnLine,
  type ClientFuture,
  type ClientGroups,
  type ClientPost,
  type ClientTurnTone,
} from "../clientReviewModel";
import "./clientReview.css";

export interface ClientListProps {
  brandName: string;
  monthTitle: string;
  sentence: string;
  groups: ClientGroups;
  selectedId: string | null;
  onSelect: (slotId: string) => void;
  readerName: string;
  /** Thread count per post. */
  counts: Record<string, number>;
  now: number;
  future?: ClientFuture;
  /** "Not you?": sign out, so a colleague can sign in. Absent hides it. */
  onSwitchReader?: () => void;
}

function day(iso: string) {
  const date = new Date(iso);
  return {
    numeral: date.getUTCDate(),
    weekday: date.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" }),
    month: date.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" }),
  };
}

/** Brand tile: a flat round disc with the brand's two letters. */
export function BrandMark({ brandName, size = 28, fontSize = 14 }: { brandName: string; size?: number; fontSize?: number }) {
  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-pill bg-co-surface-solid font-section font-extrabold leading-none tracking-[0.3px] text-co-text shadow-[inset_0_0_0_1px_var(--co-line2)]"
      style={{ width: size, height: size, fontSize }}
    >
      {initials(brandName)}
    </span>
  );
}

/** The turn dot: filled for your turn and done, hollow while a post still waits late. */
export function TurnDot({ tone }: { tone: ClientTurnTone }) {
  return <span className="co-cr-dot" data-tone={tone} aria-hidden="true" />;
}

function Row({
  post,
  selected,
  onSelect,
  count,
  now,
  future,
  phone,
}: {
  post: ClientPost;
  selected: boolean;
  onSelect: () => void;
  count: number;
  now: number;
  future?: ClientFuture;
  phone: boolean;
}) {
  const { numeral, weekday } = day(post.slot.scheduledAt);
  const turn = turnLine(post, now, future);
  const title = post.slot.idea?.title || post.slot.title || "Post";
  const comments =
    count > 0 ? (
      <span
        className={cn("flex shrink-0 items-center gap-1 text-co-muted", phone ? "text-[13px]" : "text-[12px]")}
        aria-label={`${count} ${count === 1 ? "comment" : "comments"}`}
      >
        <MessageSquare className={phone ? "size-[13px]" : "size-3"} strokeWidth={1.8} aria-hidden="true" />
        {count}
      </span>
    ) : null;
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected || undefined}
        className={cn(
          "co-cr-row flex w-full gap-3 text-left outline-none",
          phone ? "items-center px-4 py-3 shadow-[inset_0_-1px_0_var(--co-line)]" : "items-start px-5 py-3",
        )}
      >
        <span className={cn("flex shrink-0 flex-col items-center", phone ? "w-9" : "w-[34px]")}>
          <span
            className={cn(
              "font-section font-bold leading-[1.2] tabular-nums",
              phone ? "text-[24px]" : "text-[22px]",
              selected && !phone ? "text-co-accent" : "text-co-text",
            )}
          >
            {numeral}
          </span>
          <span className="text-[12.5px] leading-[1.35] text-co-muted">{weekday}</span>
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className={cn("line-clamp-2 font-semibold leading-[1.35] text-co-text", phone ? "text-[15px]" : "text-[14px]")}>
            {title}
          </span>
          {phone ? null : <span className="truncate text-[12.5px] text-co-muted">{rowMeta(post, future)}</span>}
          <span className="flex items-center gap-1.5">
            <TurnDot tone={turn.tone} />
            <span
              className={cn(
                "min-w-0 flex-1 truncate",
                phone ? "text-[13px]" : "text-[12.5px]",
                turn.tone === "you" ? "text-co-accent-ink" : turn.tone === "late" ? "text-co-alert-ink" : "text-co-text2",
              )}
            >
              {turn.label}
            </span>
            {comments}
          </span>
        </span>
        {phone ? <ChevronRight className="size-4 shrink-0 text-co-faint" strokeWidth={1.8} aria-hidden="true" /> : null}
      </button>
    </li>
  );
}

function Group({
  title,
  posts,
  ...rest
}: { title: string; posts: ClientPost[] } & Omit<ClientListProps, "groups" | "brandName" | "monthTitle" | "sentence" | "readerName"> & {
    phone: boolean;
  }) {
  if (!posts.length) return null;
  return (
    <section aria-label={title}>
      <h2
        className={cn(
          "flex items-center gap-2 font-body text-[12.5px] font-normal text-co-muted",
          rest.phone ? "px-4 pb-1.5 pt-4" : "px-5 pb-1.5 pt-3.5",
        )}
      >
        {title}
        <span className="tabular-nums text-co-faint">{posts.length}</span>
      </h2>
      <ul className="flex flex-col">
        {posts.map((post) => (
          <Row
            key={post.slot.id}
            post={post}
            selected={rest.selectedId === post.slot.id}
            onSelect={() => rest.onSelect(post.slot.id)}
            count={rest.counts[post.slot.id] ?? 0}
            now={rest.now}
            future={rest.future}
            phone={rest.phone}
          />
        ))}
      </ul>
    </section>
  );
}

/** Bar / Client top · Phone: the brand in a glass capsule and the reader in a glass circle. */
function PhoneTop({ brandName, readerName, onSwitchReader }: { brandName: string; readerName: string; onSwitchReader?: () => void }) {
  return (
    <header className="sticky top-0 z-20 flex h-[68px] items-center gap-2 p-3">
      <span className="co-glass-chrome flex h-11 min-w-0 items-center gap-2.5 rounded-pill pl-2 pr-3.5">
        <BrandMark brandName={brandName} fontSize={12} />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[15px] font-semibold leading-[1.25] text-co-text">{brandName}</span>
          <span className="truncate text-[12px] leading-[1.25] text-co-muted">For review · by {AGENCY}</span>
        </span>
      </span>
      <span className="flex-1" />
      <button
        type="button"
        onClick={onSwitchReader}
        disabled={!onSwitchReader}
        aria-label={onSwitchReader ? `${readerName}. Not you? Sign out` : readerName}
        className="co-glass-chrome grid size-11 shrink-0 place-items-center rounded-pill outline-none focus-visible:outline-2 focus-visible:outline-co-accent"
      >
        <span className="grid size-7 place-items-center rounded-[12px] bg-co-tonal text-[12px] font-bold text-co-text2">
          {initials(readerName)}
        </span>
      </button>
    </header>
  );
}

/** The whole list: 360 on the desk, the full screen on a phone. */
export function ClientPostList({ phone = false, ...props }: ClientListProps & { phone?: boolean }) {
  const { brandName, monthTitle, sentence, groups, readerName, onSwitchReader } = props;
  const empty = !groups.review.length && !groups.upcoming.length && !groups.approved.length;
  const groupsView = empty ? (
    <p className={cn("pt-8 text-[13.5px] text-co-muted", phone ? "px-4" : "px-5")}>Nothing has been sent for your review yet.</p>
  ) : (
    <>
      <Group title="For your review" posts={groups.review} {...props} phone={phone} />
      <Group title="Coming up" posts={groups.upcoming} {...props} phone={phone} />
      <Group title="Approved" posts={groups.approved} {...props} phone={phone} />
    </>
  );

  if (phone) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-co-ground pb-6">
        <PhoneTop brandName={brandName} readerName={readerName} onSwitchReader={onSwitchReader} />
        <div className="flex flex-col gap-1 px-4 pb-2 pt-[18px]">
          <h1 className="font-section text-[30px] font-bold leading-[1.2] tracking-normal text-co-text">{monthTitle}</h1>
          <p className="text-[14px] leading-[1.4] text-co-text2">{sentence}</p>
        </div>
        {groupsView}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-2.5 px-5 py-3.5 shadow-[inset_0_-1px_0_var(--co-line)]">
        <BrandMark brandName={brandName} />
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="truncate text-[14px] font-semibold leading-[1.35] text-co-text">{brandName}</span>
          <span className="truncate text-[12.5px] leading-[1.35] text-co-muted">For review · by {AGENCY}</span>
        </span>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <div className="flex flex-col gap-1 px-5 pb-2.5 pt-[18px]">
          <h1 className="font-section text-[24px] font-bold leading-[1.2] tracking-normal text-co-text">{monthTitle}</h1>
          <p className="text-[13px] leading-[1.4] text-co-text2">{sentence}</p>
        </div>
        {groupsView}
      </div>
      <footer className="flex items-center gap-2 px-5 py-3.5 shadow-[inset_0_1px_0_var(--co-line)]">
        <Avatar name={readerName} />
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-co-text">{readerName.split(/\s+/)[0]}</span>
        {onSwitchReader ? (
          <button
            type="button"
            onClick={onSwitchReader}
            className="rounded-tip text-[13px] font-medium text-co-muted outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            Not you?
          </button>
        ) : null}
      </footer>
    </div>
  );
}

/** The list folded to its dates: 72 wide, a day and a turn dot per post, a break before the posts that do not wait. */
export function ClientDateColumn({
  brandName,
  groups,
  selectedId,
  onSelect,
  onUnfold,
  readerName,
  now,
  future,
}: Pick<ClientListProps, "brandName" | "groups" | "selectedId" | "onSelect" | "readerName" | "now" | "future"> & {
  onUnfold: () => void;
}) {
  const review = groups.review;
  const rest = [...groups.upcoming, ...groups.approved];
  const first = review[0] ?? rest[0];
  const firstMonth = first ? day(first.slot.scheduledAt).month : null;

  const dateButton = (post: ClientPost, waiting: boolean) => {
    const { numeral, weekday } = day(post.slot.scheduledAt);
    const selected = post.slot.id === selectedId;
    const turn = turnLine(post, now, future);
    return (
      <li key={post.slot.id}>
        <button
          type="button"
          onClick={() => onSelect(post.slot.id)}
          aria-current={selected || undefined}
          aria-label={`${weekday} ${numeral}, ${post.slot.idea?.title || post.slot.title}, ${turn.label}`}
          className="co-cr-day flex w-[72px] flex-col items-center gap-[3px] py-2.5 outline-none"
        >
          <span
            className={cn(
              "font-section text-[20px] font-bold leading-[1.2] tabular-nums",
              selected ? "text-co-accent" : "text-co-text",
            )}
          >
            {numeral}
          </span>
          {/* A filled dot: the post is ready; an open one: it still waits. */}
          <span className="co-cr-dot" data-tone={selected ? "selected" : waiting ? "waiting" : "none"} aria-hidden="true" />
        </button>
      </li>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col items-center gap-1 py-3.5">
      <BrandMark brandName={brandName} />
      <span className="h-3.5 w-px" aria-hidden="true" />
      {firstMonth ? <p className="text-[12.5px] text-co-muted">{firstMonth}</p> : null}
      <nav aria-label="Posts" className="flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto">
        <ul className="flex flex-col gap-1">{review.map((post) => dateButton(post, true))}</ul>
        {review.length && rest.length ? <span className="my-1 h-px w-7 shrink-0 bg-co-line2" aria-hidden="true" /> : null}
        <ul className="flex flex-col gap-1">{rest.map((post) => dateButton(post, false))}</ul>
      </nav>
      <button
        type="button"
        onClick={onUnfold}
        aria-label="Open the list"
        className="grid size-8 place-items-center rounded-pill text-co-muted outline-none hover:bg-co-hover hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
      >
        <PanelLeftOpen className="size-4" strokeWidth={1.75} aria-hidden="true" />
      </button>
      <span className="h-2 w-px" aria-hidden="true" />
      <Avatar name={readerName} />
    </div>
  );
}
